import { fetchConfig } from '@theme/utilities';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * Köttbutiken: gåvor från varukorgens delmål (snippets/kb-delmal.liquid, docs/delmal.md).
 *
 * När varukorgen når gränsen för ett delmål av typen Gratis produkt läggs gåvan till som egen rad med den
 * dolda egenskapen `_kb_delmal`. Går varukorgen under gränsen tas gåvan bort. Gåvan får aldrig kosta något:
 * om Shopify inte har gjort raden gratis (rabatten saknas eller har fel gräns) tas den bort. Tar kunden själv
 * bort gåvan läggs den inte till igen. Båda gäller bara tills varukorgens övriga rader ändras: då prövas gåvan
 * på nytt, så att en gång bortklickad gåva (eller en rensad varukorg med samma token) aldrig spärrar den för gott.
 *
 * Delmålen läses från `#kb-delmal-data` (renderas i varukorgslådan och på varukorgssidan). Kontrollen körs när
 * sidan laddas och efter varje ändring av varukorgen.
 */

const EGENSKAP = '_kb_delmal';
const MINNE = 'kb-delmal';

/**
 * @typedef {{ handle: string, grans: number, variant: number }} Delmal
 * @typedef {{ key: string, quantity: number, final_line_price: number, properties?: Record<string, string> | null }} Rad
 * @typedef {{ token: string, item_count: number, items_subtotal_price: number, items: Rad[] }} Varukorg
 * @typedef {{ token: string, rader: string, avbojda: string[], utanRabatt: string[] }} Minne
 * @typedef {{ typ: 'add', delmal: Delmal } | { typ: 'change', rad: Rad, antal: number }} Atgard
 */

let paGang = false;
let igen = false;
/** Gåvorna vid förra kontrollen, för att se om kunden har tagit bort en. @type {Set<string> | null} */
let forraGavor = null;
/** Gåvor som temat själv har tagit bort. */
const egnaBorttag = new Set();

/** @returns {Delmal[]} */
function lasDelmal() {
  const element = document.getElementById('kb-delmal-data');
  if (!element) return [];
  try {
    const lista = JSON.parse(element.textContent || '[]');
    return Array.isArray(lista) ? lista : [];
  } catch {
    return [];
  }
}

/**
 * @param {string} token
 * @returns {Minne}
 */
function lasMinne(token) {
  try {
    const minne = JSON.parse(localStorage.getItem(MINNE) || 'null');
    if (minne && minne.token === token) return minne;
  } catch {
    // Privat läge eller blockerad lagring: inget minne
  }
  return { token, rader: '', avbojda: [], utanRabatt: [] };
}

/**
 * Varukorgens rader utom gåvorna, som jämförbar text.
 * @param {Varukorg} varukorg
 */
function ovrigaRader(varukorg) {
  return varukorg.items
    .filter((rad) => !gavaFor(rad))
    .map((rad) => `${rad.key}:${rad.quantity}`)
    .sort()
    .join(',');
}

/** @param {Minne} minne */
function sparaMinne(minne) {
  try {
    localStorage.setItem(MINNE, JSON.stringify(minne));
  } catch {
    // Ignoreras
  }
}

/** @returns {Promise<Varukorg>} */
async function hamtaVarukorg() {
  const svar = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  return svar.json();
}

/** @param {Rad} rad */
function gavaFor(rad) {
  return rad.properties?.[EGENSKAP] || '';
}

/**
 * Bestämmer nästa ändring, en i taget.
 * @param {Varukorg} varukorg
 * @param {Delmal[]} delmal
 * @param {Minne} minne
 * @returns {Atgard | null}
 */
function planera(varukorg, delmal, minne) {
  const gavorader = varukorg.items.filter((rad) => gavaFor(rad));
  const underlag = varukorg.items_subtotal_price - gavorader.reduce((summa, rad) => summa + rad.final_line_price, 0);
  const andraRader = varukorg.items.length - gavorader.length;

  for (const rad of gavorader) {
    const handle = gavaFor(rad);
    const mal = delmal.find((d) => d.handle === handle);

    // Gåvan kostar pengar: rabatten saknas, så den tas bort
    if (rad.final_line_price > 0) {
      if (!minne.utanRabatt.includes(handle)) minne.utanRabatt.push(handle);
      console.warn(`[kb-delmal] Gåvan för delmålet "${handle}" blev inte gratis i varukorgen och togs bort. Kontrollera den automatiska rabatten.`);
      return { typ: 'change', rad, antal: 0 };
    }
    if (andraRader === 0 || (mal && underlag < mal.grans)) {
      return { typ: 'change', rad, antal: 0 };
    }
    if (rad.quantity > 1) {
      return { typ: 'change', rad, antal: 1 };
    }
  }

  if (andraRader === 0) return null;

  for (const mal of delmal) {
    if (underlag < mal.grans) continue;
    if (gavorader.some((rad) => gavaFor(rad) === mal.handle)) continue;
    if (minne.avbojda.includes(mal.handle) || minne.utanRabatt.includes(mal.handle)) continue;
    return { typ: 'add', delmal: mal };
  }

  return null;
}

/** Varukorgssektionerna på sidan, så att lådan och sidan renderas om efter ändringen. */
function sektioner() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean)
    .join(',');
}

/**
 * Gör ändringen utan att rendera om, så att en gåva som visar sig kosta pengar aldrig syns.
 * @param {Atgard} atgard
 * @returns {Promise<{ varukorg: Varukorg, sections: Record<string, string> | undefined }>}
 */
async function utfor(atgard) {
  const sections = sektioner();

  /** @type {Response} */
  let svar;
  if (atgard.typ === 'add') {
    svar = await fetch(
      Theme.routes.cart_add_url,
      fetchConfig('json', {
        body: JSON.stringify({
          items: [{ id: atgard.delmal.variant, quantity: 1, properties: { [EGENSKAP]: atgard.delmal.handle } }],
          sections,
          sections_url: window.location.pathname,
        }),
      })
    );
  } else {
    if (atgard.antal === 0) egnaBorttag.add(gavaFor(atgard.rad));
    svar = await fetch(
      Theme.routes.cart_change_url,
      fetchConfig('json', {
        body: JSON.stringify({
          id: atgard.rad.key,
          quantity: atgard.antal,
          sections,
          sections_url: window.location.pathname,
        }),
      })
    );
  }

  const data = await svar.json();
  if (data.status || data.errors) throw new Error(data.description || data.message || String(data.errors));

  return { varukorg: await hamtaVarukorg(), sections: data.sections };
}

/**
 * Renderar om lådan och sidan (cart-items-component) och varukorgsikonen en gång, när alla ändringar är gjorda.
 * 'update' i stället för 'add', så att lådan inte öppnas av sig själv.
 * @param {Varukorg} varukorg
 * @param {Record<string, string> | undefined} sections
 */
function rendera(varukorg, sections) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: varukorg.items.map((rad) => ({ id: rad.key, quantity: rad.quantity })),
      promise: deferred.promise,
    })
  );
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(varukorg),
    detail: {
      items: varukorg.items,
      itemCount: varukorg.item_count,
      source: 'kb-delmal',
      sections,
      didError: false,
    },
  });
}

async function kontrollera() {
  if (paGang) {
    igen = true;
    return;
  }
  const delmal = lasDelmal();
  paGang = true;

  try {
    let varukorg = await hamtaVarukorg();
    const minne = lasMinne(varukorg.token);
    const gavor = new Set(varukorg.items.map(gavaFor).filter(Boolean));

    const rader = ovrigaRader(varukorg);
    if (minne.rader !== rader) {
      // Kunden har ändrat något annat än gåvan: pröva alla gåvor på nytt
      minne.rader = rader;
      minne.avbojda = [];
      minne.utanRabatt = [];
    } else if (forraGavor) {
      // En gåva som fanns förra gången och som temat inte tog bort har kunden tagit bort själv
      for (const handle of forraGavor) {
        if (!gavor.has(handle) && !egnaBorttag.has(handle) && !minne.avbojda.includes(handle)) {
          minne.avbojda.push(handle);
        }
      }
    }
    egnaBorttag.clear();

    if (delmal.length || gavor.size) {
      /** @type {Record<string, string> | undefined} */
      let sections;
      let andrad = false;
      try {
        for (let varv = 0; varv < 4; varv++) {
          const atgard = planera(varukorg, delmal, minne);
          if (!atgard) break;
          ({ varukorg, sections } = await utfor(atgard));
          andrad = true;
        }
      } finally {
        if (andrad) rendera(varukorg, sections);
      }
    }

    sparaMinne(minne);
    forraGavor = new Set(varukorg.items.map(gavaFor).filter(Boolean));
    egnaBorttag.clear();
  } catch (error) {
    console.warn('[kb-delmal]', error);
  } finally {
    paGang = false;
    if (igen) {
      igen = false;
      kontrollera();
    }
  }
}

/** @param {Event} event */
function efterUppdatering(event) {
  const promise = /** @type {{ promise?: Promise<{ detail?: { source?: string, didError?: boolean } }> }} */ (event)
    .promise;
  if (!promise) {
    kontrollera();
    return;
  }
  promise
    .then(({ detail } = {}) => {
      if (detail?.source === 'kb-delmal' || detail?.didError) return;
      kontrollera();
    })
    .catch(() => {});
}

if (!window.kbDelmal) {
  window.kbDelmal = { kontrollera };
  document.addEventListener(StandardEvents.cartLinesUpdate, efterUppdatering);
  document.addEventListener(StandardEvents.cartDiscountUpdate, efterUppdatering);
  kontrollera();
}
